import { useQuery, QueryFunctionContext, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { ShipmentOrderFormData } from 'src/components/V2ShipmentOrderForm';
import { ShipmentOrderListResponse } from 'src/entities/ShipmentOrderList.entity';
import { ShipmentDetailFormData, ShipmentOrderResponseEntity } from 'src/entities/ShipmentOrderResponse.entity';
import requestUtil from 'src/utils/request.util';

import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from './useConvenienceDispatchDeliveries.query';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from './useQueryShipmentOrderLegRuns';

export const SHIPMENT_ORDERS_QUERY_KEY = 'shipment-orders';

export type UseShipmentOrdersParams = {
  page: number;
  perPage: number;
  startOn?: string;
  endOn?: string;
  search?: string;
  filters?: string;
  sortCondition?: string;
};

type ShipmentOrdersQueryKey = [typeof SHIPMENT_ORDERS_QUERY_KEY, UseShipmentOrdersParams];

const fetchShipmentOrders = async ({ queryKey }: QueryFunctionContext<ShipmentOrdersQueryKey>) => {
  const [, params] = queryKey;
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page));
  searchParams.set('per', String(params.perPage));
  if (params.startOn) searchParams.set('start_on', params.startOn);
  if (params.endOn) searchParams.set('end_on', params.endOn);
  if (params.search) searchParams.set('keywords', params.search);
  if (params.filters) {
    const filterParams = new URLSearchParams(params.filters);
    filterParams.forEach((value, key) => {
      searchParams.append(key, value);
    });
  }
  if (params.sortCondition) searchParams.set('sort_condition', params.sortCondition);

  const { data } = await axios.get<ShipmentOrderListResponse>(`/api/v5/shipment_orders?${searchParams.toString()}`);
  return data;
};

export const useQueryShipmentOrders = (params: UseShipmentOrdersParams) => (
  useQuery<ShipmentOrderListResponse, Error>({
    queryKey: [SHIPMENT_ORDERS_QUERY_KEY, params],
    queryFn: fetchShipmentOrders,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  })
);

const getShipmentOrder = (id: number) => async () => {
  const res = await axios.get<ShipmentOrderResponseEntity>(`/api/v5/shipment_orders/${id}`);
  return res.data;
};

export const useQueryShipmentOrder = (id: number) => (
  useQuery<ShipmentOrderResponseEntity, Error>({
    queryKey: [SHIPMENT_ORDERS_QUERY_KEY, id],
    queryFn: getShipmentOrder(id),
    staleTime: Infinity,
    enabled: !!id, // idが有効な場合のみクエリを実行
  })
);

export const useMutationShipmentOrder = () => {
  const queryClient = useQueryClient();

  return {
    createShipmentOrder: useMutation({
      mutationFn: (shipmentOrder: ShipmentOrderFormData) => axios.post(
        '/api/v5/shipment_orders',
        { shipment_order: requestUtil.convertKeysToSnakeCase(shipmentOrder) }
      ),

      onSuccess: (res: AxiosResponse) => {
        // 必要に応じてキャッシュを更新
        // ordersCache.addOrders([res.data]);
        void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
      },

      onError: (error: AxiosError<{data: string[]}>) => error.response?.data
    }),
    updateShipmentOrder: useMutation({
      mutationFn: (shipmentOrder: ShipmentDetailFormData) => axios.patch(
        `/api/v5/shipment_orders/${shipmentOrder.id}`,
        { shipment_order: requestUtil.convertKeysToSnakeCase(shipmentOrder) }
      ),

      onSuccess: (res: AxiosResponse) => {
        // 必要に応じてキャッシュを更新
        // ordersCache.updateOrder(res.data);
        void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
        // ConvenienceDispatches の未割当パネル / 配車表示にも反映する。
        // ALL_DELIVERIES は「本日なし判定」用なので合わせて無効化する。
        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
      },

      onError: (error: AxiosError<{data: string[]}>) => error.response?.data
    }),
    cloneShipmentOrder: useMutation({
      mutationFn: (id: number) => axios.post(
        `/api/v5/shipment_orders/${id}/clone`
      ),

      onSuccess: (res: AxiosResponse) => {
        // 必要に応じてキャッシュを更新
        // ordersCache.addOrders([res.data]);
        void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
      },

      onError: (error: AxiosError<{data: string[]}>) => error.response?.data
    }),
    bulkDelete: useMutation({
      mutationFn: (ids: number[]) => axios.delete(
        '/api/v5/shipment_orders/bulk',
        { data: { ids } }
      ),

      onSuccess: (res: AxiosResponse) => {
        // 必要に応じてキャッシュを更新
        // ordersCache.addOrders([res.data]);
        void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
        // ConvenienceDispatches の未割当パネル / 配車表示にも反映する。
        // ALL_DELIVERIES は「本日なし判定」用なので合わせて無効化する。
        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
        void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
      },

      onError: (error: AxiosError<{data: string[]}>) => error.response?.data
    }),
  };
};
